/**
 * Modal exibido quando o usuário tenta transferir/receber e tem taxas pendentes.
 * Mostra o total devido e um botão para pagar ao FEE_ADDRESS.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Send, Wallet } from 'lucide-react';
import { BrowserProvider, Contract, parseUnits } from 'ethers';
import Modal from '@/components/ui/Modal';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { usePendingFees } from '@/contexts/PendingFeeContext';
import { STABLECOIN_CONTRACTS, ERC20_TRANSFER_ABI } from '@/config/stablecoinContracts';
import { formatCurrencyDisplay } from '@/utils/currency';
import { getBlockExplorerTxUrl } from '@/utils/blockExplorer';

/**
 * variant: 'blocking' = aplicação disparou (limite atingido); 'voluntary' = usuário clicou no link para quitar.
 */
export default function PendingFeeModal({ isOpen, onClose, onPaid, variant = 'blocking' }) {
  const { t } = useTranslation();
  const {
    pendingFees,
    totalPendingUSD,
    markAllPaid,
    getFeeAddress,
    accumulationLimit,
  } = usePendingFees();

  const {
    provider: metamaskProvider,
    selectedNetwork,
    connectedWallet,
    walletStablecoinBalances,
  } = useWalletConnection();

  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const isWalletConnected = connectedWallet === 'metamask' || connectedWallet === 'walletconnect';

  /** Encontra o primeiro token (stablecoin) com saldo suficiente para pagar a taxa. */
  const getPayableToken = () => {
    const chainId = selectedNetwork?.chainId;
    const contracts = chainId ? STABLECOIN_CONTRACTS[chainId] : null;
    if (!contracts || !walletStablecoinBalances) return null;

    // Converte walletStablecoinBalances { USDC: "12.5", USDT: "0" } para lista com saldo > 0
    const tokens = Object.entries(walletStablecoinBalances)
      .filter(([_, bal]) => parseFloat(bal) > 0)
      .map(([symbol, bal]) => ({ symbol, balance: parseFloat(bal) }));

    for (const t of tokens) {
      if (contracts[t.symbol] && t.balance >= totalPendingUSD) {
        return { symbol: t.symbol, address: contracts[t.symbol], balance: t.balance };
      }
    }
    // Fallback: primeiro stablecoin disponível (mesmo sem saldo suficiente — mostrará erro)
    for (const t of tokens) {
      if (contracts[t.symbol]) {
        return { symbol: t.symbol, address: contracts[t.symbol], balance: t.balance };
      }
    }
    return null;
  };

  const payableToken = isOpen ? getPayableToken() : null;
  const feeAddress = selectedNetwork ? getFeeAddress(selectedNetwork.chainId) : null;

  const handlePay = async () => {
    if (!metamaskProvider || !feeAddress || !payableToken) return;
    setPaying(true);
    setError(null);

    try {
      const ethersProvider = new BrowserProvider(metamaskProvider);
      const signer = await ethersProvider.getSigner();
      const contract = new Contract(payableToken.address, ERC20_TRANSFER_ABI, signer);

      let decimals = 6;
      try {
        decimals = Number(await contract.decimals());
      } catch { /* padrão */ }

      const amountStr = totalPendingUSD.toFixed(decimals);
      const amountUnits = parseUnits(amountStr, decimals);

      const tx = await contract.transfer(feeAddress, amountUnits);
      await tx.wait();

      markAllPaid(tx.hash);
      onPaid?.();
      onClose();
    } catch (err) {
      console.error('[PendingFeeModal] Erro ao pagar taxa:', err);
      if (err?.code === 'ACTION_REJECTED' || err?.code === 4001) {
        setError(t('modal.paymentCancelled'));
      } else {
        setError(err?.message || t('modal.payFeeError'));
      }
    } finally {
      setPaying(false);
    }
  };

  if (!isOpen) return null;

  const isBlocking = variant === 'blocking';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isBlocking ? t('modal.pendingFeeTitle') : t('modal.payPendingFeesTitle')}
    >
      <div className="flex flex-col items-center gap-4">
        {isBlocking ? (
          <div className="w-14 h-14 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <AlertTriangle size={32} className="text-yellow-400" />
          </div>
        ) : (
          <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center">
            <Wallet size={32} className="text-purple-400" />
          </div>
        )}

        {isBlocking && (
          <p className="text-gray-300 text-center text-sm">
            {accumulationLimit > 0 ? (
              t('modal.limitReached', { amount: formatCurrencyDisplay(accumulationLimit, 'USD') })
            ) : (
              pendingFees.length === 1
                ? t('modal.oneFeePending')
                : t('modal.feesPending', { count: pendingFees.length })
            )}
          </p>
        )}

        <p className="text-2xl font-bold text-white text-center">
          {isBlocking ? formatCurrencyDisplay(totalPendingUSD, 'USD') : t('modal.totalToPay', { amount: formatCurrencyDisplay(totalPendingUSD, 'USD') })}
        </p>

        {/* Lista de taxas com link da transação de origem */}
        {pendingFees.length >= 1 && (
          <div className="w-full max-h-40 overflow-y-auto bg-gray-900/60 rounded-xl p-3 space-y-2">
            {pendingFees.map((f) => {
              const explorerUrl = getBlockExplorerTxUrl(f.chainId, f.originTxHash);
              return (
                <div key={f.id} className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-gray-400">
                  <span>{new Date(f.createdAt).toLocaleDateString('pt-BR')}</span>
                  <span className="font-mono">{formatCurrencyDisplay(f.feeUSD, 'USD')} {f.token || ''}</span>
                  {explorerUrl ? (
                    <a
                      href={explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-400 hover:text-purple-300 underline shrink-0"
                    >
                      {t('transfer.viewOnBlockExplorer')}
                    </a>
                  ) : (
                    f.originTxHash && (
                      <span className="font-mono truncate max-w-[120px]" title={f.originTxHash}>
                        {f.originTxHash.slice(0, 10)}…
                      </span>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}

        {error && (
          <p className="text-red-400 text-sm text-center">{error}</p>
        )}

        {!isWalletConnected && (
          <p className="text-yellow-400 text-sm text-center">{t('modal.connectWalletToPay')}</p>
        )}

        {isWalletConnected && !payableToken && (
          <p className="text-yellow-400 text-sm text-center">
            {t('modal.noStablecoinOnNetwork')}
          </p>
        )}

        <button
          type="button"
          disabled={paying || !isWalletConnected || !payableToken}
          onClick={handlePay}
          className="w-full py-3 px-4 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white font-medium rounded-xl transition flex items-center justify-center gap-2"
        >
          {paying ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {t('modal.paying')}
            </>
          ) : (
            <>
              <Send size={18} />
              {t('modal.payFee')} {payableToken ? `(${payableToken.symbol})` : ''}
            </>
          )}
        </button>

        <button
          type="button"
          onClick={onClose}
          disabled={paying}
          className="w-full py-2 text-sm text-gray-400 hover:text-white transition"
        >
          {t('modal.cancel')}
        </button>
      </div>
    </Modal>
  );
}
